
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Calendar, Clock, User, CheckCircle, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { getSessionBookings, updateSessionBookingStatus, SessionBookingWithMentee } from '@/utils/sessionBookingsUtils';
import { format, parseISO } from 'date-fns';

const SessionBookingsView = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [bookings, setBookings] = useState<SessionBookingWithMentee[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadBookings();
    }
  }, [user]);

  const loadBookings = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const result = await getSessionBookings(user.id);
      if (result.success && result.data) {
        setBookings(result.data);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (bookingId: string, status: 'confirmed' | 'cancelled') => {
    try {
      const result = await updateSessionBookingStatus(bookingId, status);
      if (result.success) {
        toast({
          title: "Booking Updated",
          description: `Session has been ${status}.`,
        });
        loadBookings();
      } else {
        throw new Error('Failed to update booking');
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      toast({
        title: "Error",
        description: "Failed to update booking. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const upcomingBookings = bookings.filter(booking => 
    new Date(booking.booked_date) >= new Date() && 
    booking.status !== 'cancelled'
  );

  const pastBookings = bookings.filter(booking => 
    new Date(booking.booked_date) < new Date() || 
    booking.status === 'cancelled'
  );

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-8 h-8 border-4 border-emu-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const renderBookingCard = (booking: SessionBookingWithMentee) => (
    <Card key={booking.id} className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarImage src={booking.mentee.avatar_url} />
            <AvatarFallback>
              {booking.mentee.first_name[0]}{booking.mentee.last_name[0]}
            </AvatarFallback>
          </Avatar>
          
          <div>
            <h3 className="font-medium">
              {booking.mentee.first_name} {booking.mentee.last_name}
            </h3>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {format(parseISO(booking.booked_date), 'PPP')}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {booking.start_time} - {booking.end_time}
              </span>
            </div>
            {booking.notes && (
              <p className="text-sm text-gray-600 mt-1">{booking.notes}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge className={getStatusColor(booking.status)}>
            {booking.status}
          </Badge>
          
          {booking.status === 'pending' && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleStatusUpdate(booking.id, 'confirmed')}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Confirm
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleStatusUpdate(booking.id, 'cancelled')}
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Upcoming Bookings ({upcomingBookings.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingBookings.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No upcoming bookings</p>
          ) : (
            <div className="space-y-3">
              {upcomingBookings.map(renderBookingCard)}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Past Bookings</CardTitle>
        </CardHeader>
        <CardContent>
          {pastBookings.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No past bookings</p>
          ) : (
            <div className="space-y-3">
              {pastBookings.slice(0, 10).map(renderBookingCard)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SessionBookingsView;
